import React from "react";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { MONTHS, MON_SHORT, startOfWeek, addDays, sameDay, startOfDay } from "./scheduleUtils";

// Faithful port of the prototype's buildWeekPicker: a week/month picker with a
// presets rail, month/year header, a 6x7 grid that highlights the whole
// selected week as a black pill (rounded at the week ends), and a footer with
// the range + Cancel/Apply. Clicking any day picks that week (or month).
export default function WeekPicker({ monthDate, selWeek, selDay, maxDate, mode = "week", onMonthChange, onPick, onClose }) {
  const isMonth = mode === "month";
  const isDay = mode === "day";
  const today = startOfDay(new Date());
  const base = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const thisWk = startOfWeek(today);
  const lastWk = addDays(thisWk, -7);
  const yest = addDays(today, -1);
  const day = isDay ? startOfDay(selDay || today) : today;
  const maxD = maxDate ? startOfDay(maxDate) : null;
  const tMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

  let presets;
  if (isMonth) {
    presets = [
      { k: "thismonth", label: "This Month", on: () => onPick(new Date(today.getFullYear(), today.getMonth(), 1)) },
      { k: "lastmonth", label: "Last Month", on: () => onPick(new Date(today.getFullYear(), today.getMonth() - 1, 1)) },
    ];
  } else if (isDay) {
    presets = [
      { k: "today", label: "Today", on: () => onPick(new Date(today)) },
      { k: "yesterday", label: "Yesterday", on: () => onPick(new Date(yest)) },
    ];
  } else {
    presets = [
      { k: "thisweek", label: "This Week", on: () => onPick(new Date(today)) },
      { k: "lastweek", label: "Last Week", on: () => onPick(addDays(today, -7)) },
    ];
  }
  let activePreset;
  if (isMonth) activePreset = sameMonth(base, tMonth) ? "thismonth" : sameMonth(base, lMonth) ? "lastmonth" : "custom";
  else if (isDay) activePreset = sameDay(day, today) ? "today" : sameDay(day, yest) ? "yesterday" : "custom";
  else activePreset = sameDay(selWeek, thisWk) ? "thisweek" : sameDay(selWeek, lastWk) ? "lastweek" : "custom";

  const gridStart = startOfWeek(base);
  // Only the weeks this month spans (5 for most, 6 when a long month wraps) —
  // never a trailing all-next-month row.
  const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const totalCells = Math.ceil((base.getDay() + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));
  const wkEnd = addDays(selWeek, 6);
  let rangeTxt;
  if (isMonth) rangeTxt = `${MONTHS[base.getMonth()]} ${base.getFullYear()}`;
  else if (isDay) rangeTxt = `${MON_SHORT[day.getMonth()]} ${day.getDate()}, ${day.getFullYear()}`;
  else rangeTxt = `${MON_SHORT[selWeek.getMonth()]} ${selWeek.getDate()} - ${MON_SHORT[wkEnd.getMonth()]} ${wkEnd.getDate()}, ${wkEnd.getFullYear()}`;

  const presetBtns = [...presets];
  if (!isMonth) presetBtns.push({ k: "custom", label: "Custom", on: () => {} });

  return (
    <div className={css.dpPop}>
      <div className={css.dpRail}>
        {presetBtns.map((pr) => (
          <button
            key={pr.k}
            type="button"
            className={`${css.dpPreset} ${activePreset === pr.k ? css.active : ""}`}
            onClick={(e) => { e.stopPropagation(); pr.on(); }}
          >
            {pr.label}
          </button>
        ))}
      </div>
      <div className={css.dpMain}>
        <div className={css.dpTitle}>{isMonth ? "Select Month" : isDay ? "Select Date" : "Select Week"}</div>
        <div className={css.dpHead}>
          <button type="button" className={css.dpNav} onClick={(e) => { e.stopPropagation(); onMonthChange(new Date(base.getFullYear(), base.getMonth() - 1, 1)); }}>
            <Icon name="chevronLeft" size={16} />
          </button>
          <span className={css.dpMon}>{MONTHS[base.getMonth()]}</span>
          <span className={css.dpYr}>{base.getFullYear()}</span>
          <button type="button" className={css.dpNav} onClick={(e) => { e.stopPropagation(); onMonthChange(new Date(base.getFullYear(), base.getMonth() + 1, 1)); }}>
            <Icon name="chevron" size={16} />
          </button>
        </div>
        <div className={css.dpGrid}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className={css.dpDow}>{d}</div>
          ))}
          {cells.map((d) => {
            const out = d.getMonth() !== base.getMonth();
            const isToday = sameDay(d, today);
            const dd = d.getDay();
            const isFirst = d.getDate() === 1;
            const isLast = d.getMonth() !== addDays(d, 1).getMonth();
            const disabled = maxD ? startOfDay(d) > maxD : false;
            const inSel = isDay ? sameDay(d, day) : isMonth ? !out : sameDay(startOfWeek(d), selWeek);
            const wkStart = isDay ? inSel : dd === 0 || (isMonth && inSel && isFirst);
            const wkEndCell = isDay ? inSel : dd === 6 || (isMonth && inSel && isLast);
            return (
              <button
                key={d.toISOString()}
                type="button"
                disabled={disabled}
                className={[
                  css.dpCell,
                  out ? css.out : "",
                  isToday ? css.today : "",
                  inSel ? css.inWeek : "",
                  wkStart ? css.wkStart : "",
                  wkEndCell ? css.wkEnd : "",
                ].join(" ")}
                onClick={(e) => { e.stopPropagation(); if (!disabled) onPick(new Date(d)); }}
              >
                <span className={css.dpN}>{d.getDate()}</span>
              </button>
            );
          })}
        </div>
        <div className={css.dpFoot}>
          <span className={css.dpRange}>{rangeTxt}</span>
          <button type="button" className={css.dpCancel} onClick={(e) => { e.stopPropagation(); onClose?.(); }}>Cancel</button>
          <button type="button" className={css.dpApply} onClick={(e) => { e.stopPropagation(); onClose?.(); }}>Apply</button>
        </div>
      </div>
    </div>
  );
}
